import React, { useState } from "react";
import { useTranslate, useCustom, useApiUrl } from "@refinedev/core";
import {
    Col,
    Row,
    Statistic,
    Table,
    DatePicker,
    Typography,
    Skeleton,
    Alert,
    Divider,
    Tag,
    theme,
} from "antd";
import { GlassCard } from "../../shared/components";
import {
    ContainerOutlined,
    LockOutlined,
    FileZipOutlined,
    CalendarOutlined,
    BarChartOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

const { Title, Text } = Typography;

interface UsageLineDto {
    count: number;
    bundleSize: number | null;
    bundleFee: number | null;
    unitPrice: number;
    bundleUnitsUsed: number;
    overageCount: number;
    subtotal: number;
}

interface BillingUsageResponse {
    tenantId: number;
    tenantName: string;
    billingPeriod: {
        month: string;
        startDate: string;
        endDate: string;
    };
    usageSummary: {
        ordersCompleted: UsageLineDto;
        decryptTasksCompleted: UsageLineDto;
        filesAssembled: UsageLineDto;
    };
    grandTotal: number;
    currency: string;
}

interface UsageTableRow {
    key: string;
    category: string;
    icon: React.ReactNode;
    count: number;
    bundleSize: number | null;
    bundleFee: number | null;
    bundleUnitsUsed: number;
    overageCount: number;
    unitPrice: number;
    subtotal: number;
}

const formatCurrency = (value: number, currency: string) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(value);

export const UsagePage: React.FC<{ hideHeader?: boolean }> = ({ hideHeader }) => {
    const translate = useTranslate();
    const apiUrl = useApiUrl();
    const { token } = theme.useToken();
    const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());

    const monthParam = selectedMonth.format("YYYY-MM");

    const { data, isLoading, isFetching, isError } = useCustom<BillingUsageResponse>({
        url: `${apiUrl}/tenant-billing/usage`,
        method: "get",
        config: {
            query: { month: monthParam },
        },
        queryOptions: {
            queryKey: ["tenant-billing-usage", monthParam],
        },
    });

    const showSkeleton = isLoading || isFetching;
    const usage = showSkeleton ? undefined : data?.data;
    const currency = usage?.currency ?? "EUR";

    const tableRows: UsageTableRow[] = usage
        ? [
              {
                  key: "ordersCompleted",
                  category: translate("usage.categories.ordersCompleted"),
                  icon: <ContainerOutlined />,
                  ...usage.usageSummary.ordersCompleted,
              },
              {
                  key: "decryptTasksCompleted",
                  category: translate("usage.categories.decryptTasksCompleted"),
                  icon: <LockOutlined />,
                  ...usage.usageSummary.decryptTasksCompleted,
              },
              {
                  key: "filesAssembled",
                  category: translate("usage.categories.filesAssembled"),
                  icon: <FileZipOutlined />,
                  ...usage.usageSummary.filesAssembled,
              },
          ]
        : [];

    const statCards = [
        {
            key: "ordersCompleted",
            label: translate("usage.categories.ordersCompleted"),
            suffix: translate("usage.orders"),
            icon: <ContainerOutlined className="text-2xl" />,
            colorClass: "text-blue-500",
            data: usage?.usageSummary.ordersCompleted,
        },
        {
            key: "decryptTasksCompleted",
            label: translate("usage.categories.decryptTasksCompleted"),
            suffix: translate("usage.decryptTasks"),
            icon: <LockOutlined className="text-2xl" />,
            colorClass: "text-purple-500",
            data: usage?.usageSummary.decryptTasksCompleted,
        },
        {
            key: "filesAssembled",
            label: translate("usage.categories.filesAssembled"),
            suffix: translate("usage.filesAssembled"),
            icon: <FileZipOutlined className="text-2xl" />,
            colorClass: "text-green-500",
            data: usage?.usageSummary.filesAssembled,
        },
    ];

    const columns = [
        {
            title: translate("usage.fields.category"),
            dataIndex: "category",
            key: "category",
            render: (text: string, record: UsageTableRow) => (
                <span className="flex items-center gap-2 font-medium">
                    {record.icon}
                    {text}
                </span>
            ),
        },/*
        {
            title: translate("usage.fields.count"),
            dataIndex: "count",
            key: "count",
            align: "right" as const,
            render: (v: number) => <Text strong>{v.toLocaleString("fr-FR")}</Text>,
        },*/
        {
            title: translate("usage.fields.bundleUsage"),
            key: "bundleUsage",
            align: "right" as const,
            render: (_: unknown, record: UsageTableRow) =>
                record.bundleSize != null ? (
                    <span>
                        <Text strong>{record.bundleUnitsUsed.toLocaleString("fr-FR")}</Text>
                        <Text type="secondary"> / {record.bundleSize.toLocaleString("fr-FR")}</Text>
                    </span>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: translate("usage.fields.overageCount"),
            dataIndex: "overageCount",
            key: "overageCount",
            align: "right" as const,
            render: (v: number) =>
                v > 0 ? (
                    <Tag color="orange">{v.toLocaleString("fr-FR")}</Tag>
                ) : (
                    <Tag color="blue">0</Tag>
                ),
        },
        {
            title: translate("usage.fields.unitPrice"),
            dataIndex: "unitPrice",
            key: "unitPrice",
            align: "right" as const,
            render: (v: number) => formatCurrency(v, currency),
        },
        {
            title: translate("usage.fields.subtotal"),
            dataIndex: "subtotal",
            key: "subtotal",
            align: "right" as const,
            render: (v: number) => (
                <Text strong>{formatCurrency(v, currency)}</Text>
            ),
        },
    ];

    return (
        <div className={hideHeader ? "" : "p-6 max-w-7xl mx-auto"}>
            {/* Header — the Organisation hub renders its own title, so inside it
                only the month picker remains. */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    {!hideHeader && (
                        <>
                            <BarChartOutlined className="text-2xl" style={{ color: token.colorPrimary }} />
                            <Title level={3} className="!mb-0">
                                {translate("usage.title")}
                            </Title>
                        </>
                    )}
                </div>
                <DatePicker
                    picker="month"
                    value={selectedMonth}
                    onChange={(val) => val && setSelectedMonth(val)}
                    format="MMMM YYYY"
                    allowClear={false}
                    className="w-44"
                />
            </div>

            {/* Billing period banner */}
            {usage && (
                <div className="flex items-center gap-2 mb-6 text-gray-500">
                    <CalendarOutlined />
                    <Text type="secondary">
                        {translate("usage.billingPeriod")}:{" "}
                        <Text>
                            {dayjs(usage.billingPeriod.startDate).format("D MMM YYYY")} —{" "}
                            {dayjs(usage.billingPeriod.endDate).format("D MMM YYYY")}
                        </Text>
                    </Text>
                </div>
            )}

            {isError && (
                <Alert
                    type="error"
                    message={translate("usage.errors.fetchFailed")}
                    className="mb-6"
                />
            )}

            {/* Summary stat cards */}
            <Row gutter={[16, 16]} className="mb-6">
                {statCards.map((card) => (
                    <Col xs={24} sm={8} key={card.key}>
                        <GlassCard className="shadow-sm h-full">
                            {showSkeleton ? (
                                <Skeleton active paragraph={{ rows: 3 }} />
                            ) : (
                                <div className="flex items-start justify-between">
                                    <div>
                                        <Text type="secondary" className="text-sm">
                                            {card.label}
                                        </Text>
                                        <Statistic
                                            value={card.data?.count ?? 0}
                                            suffix={<Text type="secondary">{card.suffix}</Text>}
                                            valueStyle={{ fontSize: 28 }}
                                            className="mt-1"
                                        />
                                        <Text className={`text-base font-semibold`} style={{ color: token.colorPrimary }}>
                                            {card.data
                                                ? formatCurrency(card.data.subtotal, currency)
                                                : "—"}
                                        </Text>
                                    </div>
                                    <span className={`opacity-70`} style={{ color: token.colorPrimary }}>
                                        {card.icon}
                                    </span>
                                </div>
                            )}
                        </GlassCard>
                    </Col>
                ))}
            </Row>

            {/* Detailed breakdown table */}
            <GlassCard className="shadow-sm mb-6" title={translate("usage.breakdownTitle")}>
                {showSkeleton ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                    <Table
                        dataSource={tableRows}
                        columns={columns}
                        pagination={false}
                        rowKey="key"
                        size="middle"
                        scroll={{ x: "max-content" }}
                    />
                )}
            </GlassCard>

            {/* Grand total */}
            <GlassCard className="shadow-sm">
                {showSkeleton ? (
                    <Skeleton active paragraph={{ rows: 1 }} />
                ) : (
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <Text type="secondary">{translate("usage.grandTotalNote")}</Text>
                        <div className="flex items-center gap-3">
                            <Text className="text-lg">{translate("usage.grandTotal")}</Text>
                            <Divider type="vertical" className="h-6" />
                            <Title level={3} className="!mb-0 text-blue-600">
                                {usage ? formatCurrency(usage.grandTotal, currency) : "—"}
                            </Title>
                            <Tag style={{ color: token.colorPrimary }}>{currency}</Tag>
                        </div>
                    </div>
                )}
            </GlassCard>
        </div>
    );
};
